import React, { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { AppDispatch, RootStateType } from '@Store';
import SBDropdown from '@Components/Dropdown';
import SbButton from '@Components/Button';
import SbTextField from '@Components/Textfield';
import SBCountryDropdown from '@Components/Dropdown/Country';
import { downloadCarrierLabel } from '@Reducers/ShipmentHistory/actions';
import { setIsWarehouseSelectedINFedX } from '@Reducers/Warehouse';
import { vendorAccountType } from '@Reducers/AdminShipment/Type';
import DatePicker from '@Components/DatePicker';
import {
  onForward,
  emiratesInformationValidation,
  paymentModeOptions,
  serviceTypeOptions,
  shipmentPurposeOptions,
  // isDGOptions,
  // isDGaccessibleOptions,
  dgQuantityUnitOptions,
  shipmentTypeOptions,
  isConsigneeAddressResidentialOptions,
  isShipperAddressResidentialOptions,
} from './constants';
import UploadEmiratesDoc from './UploadEmiratesDoc';
import { setDocumentUploadEmiratesModalOpen } from '@Reducers/emiratesModalSlice/emiratesModalSlice';
import Image from '@Components/Image';
import dayjs from 'dayjs';
import { SuccessErrorModalOpen } from '@Reducers/SuccessErrorModal';

interface Props {
  onClickForward: () => void;
  onClose?: () => void;
  initialStates: any;
  isEditable?: boolean;
  showEmiratesDetails: boolean;
  btnText: string;
  setShowAWB: Dispatch<SetStateAction<string>>;
}

const EmiratesInformation: React.FC<Props> = ({
  onClickForward,
  onClose,
  initialStates,
  isEditable = false,
  showEmiratesDetails,
  btnText,
  setShowAWB,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const documentUploadEmiratesModalOpen = useSelector(
    (state: RootStateType) => state.emiratesModal.documentUploadEmiratesModalOpen,
  );
  const { isWarehouseSelectedINFedX } = useSelector((state: RootStateType) => state.Warehouse);
  const { shipmentById, vendersAccount, emiratesDetails } = useSelector((state: RootStateType) => state.adminShipment);
  const { _consigneeId } = shipmentById || {};
  const isSaudiArabia = _consigneeId?.country === 'Saudi Arabia';
  const emiratesSettings = useSelector((state: RootStateType) => state.user.carrierSetting.emirates_skycargo);

  const accountOptions = useMemo(
    () =>
      vendersAccount?.map((item: vendorAccountType) => ({
        value: `${item.accountNo}-${item._id}`,
        display: item.name,
      })) ?? [],
    [vendersAccount],
  );

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      accountNo: '',
      countryOfManufacture: '',
      paymentMode: '',
      serviceType: '',
      shipmentPurpose: '',
      isDG: false,
      isDGaccessible: false,
      isConsigneeAddressResidential: null,
      isShipperAddressResidential: false,
      shipperId: '',
      dgUnId: '',
      dgPiId: '',
      dgProperShippingName: '',
      dgClassDivision: '',
      dgPackingGroup: '',
      dgQuantity: initialStates?.dgQuantity || 0,
      dgQuantityUnit: '',
      locationIdentifier: '',
      nationalId: '',
      dateOfBirth: null,
      nationality: isSaudiArabia ? 'SA' : '',
      expiryDate: null,
      nameAsPerID: '',
      ...initialStates,
    },
    validationSchema: emiratesInformationValidation(isSaudiArabia),
    validateOnBlur: true,

    onSubmit: values => {
      if (!isWarehouseSelectedINFedX || isWarehouseSelectedINFedX === 'false') {
        dispatch(setIsWarehouseSelectedINFedX('false'));
      }

      onForward({
        values,
        shipmentNumber: shipmentById?.shipmentNumber || '',
        dispatch,
        nextFunc: onClickForward,
      });
    },
  });

  const { values, setFieldValue, errors, handleSubmit, handleChange } = formik;

  useEffect(() => {
    if (values.accountNo?.length && accountOptions?.length) {
      const matched = accountOptions.find(opt => opt.value.startsWith(values.accountNo));

      if (matched) {
        setFieldValue('accountNo', matched.value);
      }
    }
  }, [values.accountNo, accountOptions]);

  useEffect(() => {
    if (!isSaudiArabia) {
      setFieldValue('locationIdentifier', '');
      setFieldValue('nationalId', '');
      setFieldValue('dateOfBirth', null);
      setFieldValue('nationality', '');
      setFieldValue('expiryDate', null);
      setFieldValue('nameAsPerID', '');
    } else {
      // setFieldValue('locationIdentifier', '');
      setFieldValue('nationality', 'SA');
    }
  }, [isSaudiArabia]);

  if (!showEmiratesDetails) return null;

  return (
    <>
      {documentUploadEmiratesModalOpen && (
        <UploadEmiratesDoc
          shipmentNumber={shipmentById?.shipmentNumber || ''}
          csp={values?.csp}
          type={values?.type}
          open={documentUploadEmiratesModalOpen}
          handleClose={() => {
            dispatch(setDocumentUploadEmiratesModalOpen(false));
            setShowAWB('');
          }}
          emiratesDetails={emiratesDetails}
        />
      )}

      <Paper variant="outlined" sx={{ my: '2rem', p: '2rem' }}>
        <Grid container alignItems="center" justifyContent="space-between" mb={3}>
          <Grid item>
            <Typography variant="h6" gutterBottom>
              Emirates Information
            </Typography>
          </Grid>

          {emiratesDetails?.trackingNumber && (
            <Grid item>
              <SbButton
                variant="contained"
                sx={{ borderRadius: `4px !important` }}
                startIcon={<Image src={'/images/upload-cloud.svg'} alt={'documents'} width={26} />}
                onClick={() => {
                  dispatch(setDocumentUploadEmiratesModalOpen(true));
                }}
              >
                Forward Shipment Documents
              </SbButton>
            </Grid>
          )}
        </Grid>

        <Grid container spacing={4}>
          {/* Account Number */}
          <Grid item xs={12} md={6}>
            <SBDropdown
              disabled={!isEditable}
              id="accountNo"
              label="Emirates Account"
              required
              placeholder="Select Account"
              name="accountNo"
              value={values.accountNo ? [values.accountNo] : []}
              options={emiratesSettings?.accountNo?.length ? emiratesSettings?.accountNo : accountOptions}
              onChange={value => {
                setFieldValue('accountNo', value[0]);
              }}
              error={errors.accountNo as string}
            />
          </Grid>

          {/* Country of Manufacture */}
          <Grid item xs={12} md={6}>
            <SBCountryDropdown
              disabled={!isEditable}
              required
              label="Country of Manufacture"
              value={values?.countryOfManufacture}
              options={undefined}
              onChange={value => {
                setFieldValue('countryOfManufacture', value[0]);
              }}
              error={errors?.countryOfManufacture as string}
            />
          </Grid>

          {/* Payment Mode */}
          <Grid item xs={12} md={6}>
            <SBDropdown
              disabled={!isEditable}
              id="paymentMode"
              label="Payment Mode"
              required
              placeholder="Select Payment Mode"
              name="paymentMode"
              value={values.paymentMode ? [values.paymentMode] : []}
              options={paymentModeOptions}
              onChange={value => {
                setFieldValue('paymentMode', value[0]);
              }}
              error={errors.paymentMode as string}
            />
          </Grid>

          {/* Service Type */}
          <Grid item xs={12} md={6}>
            <SBDropdown
              disabled={!isEditable}
              id="serviceType"
              label="Service Type"
              required
              placeholder="Select Service Type"
              name="serviceType"
              value={values.serviceType ? [values.serviceType] : []}
              options={serviceTypeOptions}
              onChange={value => {
                setFieldValue('serviceType', value[0]);
              }}
              error={errors.serviceType as string}
            />
          </Grid>

          {/* Shipment Purpose */}
          <Grid item xs={12} md={6}>
            <SBDropdown
              disabled={!isEditable}
              id="shipmentPurpose"
              label="Shipment Purpose"
              required
              placeholder="Select Shipment Purpose"
              name="shipmentPurpose"
              value={values.shipmentPurpose ? [values.shipmentPurpose] : []}
              options={shipmentPurposeOptions}
              onChange={value => {
                setFieldValue('shipmentPurpose', value[0]);
              }}
              error={errors.shipmentPurpose as string}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <SBDropdown
              disabled={!isEditable}
              id="shipmentType"
              label="Shipment Type"
              required
              placeholder="Select Shipment Type"
              name="shipmentType"
              value={values.shipmentType ? [values.shipmentType] : []}
              options={shipmentTypeOptions}
              onChange={value => {
                setFieldValue('shipmentType', value[0]);
              }}
              error={errors.shipmentType as string}
            />
          </Grid>

          {/* isDG - Dangerous Goods */}
          {/* <Grid item xs={12} md={6}>
            <SBDropdown
              disabled={!isEditable}
              id="isDG"
              label="Dangerous Goods (DG)"
              required
              placeholder="Select"
              name="isDG"
              value={values.isDG !== undefined ? [values.isDG.toString()] : ['false']}
              options={isDGOptions}
              onChange={value => {
                const boolValue = value[0] === 'true';
                setFieldValue('isDG', boolValue);
                // Reset DG fields when isDG is set to false
                if (!boolValue) {
                  setFieldValue('dgUnId', '');
                  setFieldValue('dgPiId', '');
                  setFieldValue('dgProperShippingName', '');
                  setFieldValue('dgClassDivision', '');
                  setFieldValue('dgPackingGroup', '');
                  setFieldValue('dgQuantity', 0);
                  setFieldValue('dgQuantityUnit', '');
                }
              }}
              error={errors.isDG as string}
            />
          </Grid> */}

          {/* isDGaccessible - DG Accessible */}
          {/* <Grid item xs={12} md={6}>
            <SBDropdown
              disabled={!isEditable}
              id="isDGaccessible"
              label="DG Accessible"
              required
              placeholder="Select"
              name="isDGaccessible"
              value={values.isDGaccessible !== undefined ? [values.isDGaccessible.toString()] : ['false']}
              options={isDGaccessibleOptions}
              onChange={value => {
                const boolValue = value[0] === 'true';
                setFieldValue('isDGaccessible', boolValue);
              }}
              error={errors.isDGaccessible as string}
            />
          </Grid> */}

          <Grid item xs={12} md={6}>
            <SBDropdown
              disabled={!isEditable}
              id="isShipperAddressResidential"
              label="Is Shipper Address Residential?"
              required
              placeholder="Select"
              name="isShipperAddressResidential"
              value={[String(values.isShipperAddressResidential)]}
              options={isShipperAddressResidentialOptions}
              onChange={value => {
                setFieldValue('isShipperAddressResidential', value[0] === 'true');
              }}
              error={errors.isShipperAddressResidential as string}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <SBDropdown
              disabled={!isEditable}
              id="isConsigneeAddressResidential"
              label="Is Consignee Address Residential?"
              required
              placeholder="Select"
              name="isConsigneeAddressResidential"
              value={
                values.isConsigneeAddressResidential !== null ? [String(values.isConsigneeAddressResidential)] : []
              }
              options={isConsigneeAddressResidentialOptions}
              onChange={value => {
                setFieldValue('isConsigneeAddressResidential', value[0] === 'true');
              }}
              error={errors.isConsigneeAddressResidential as string}
            />
          </Grid>

          {/* Shipper ID (Optional) */}
          <Grid item xs={12} md={6}>
            <SbTextField
              disabled={!isEditable}
              label="Shipper ID"
              value={values.shipperId}
              placeholder="Enter Shipper ID"
              onChange={handleChange}
              name="shipperId"
              error={errors.shipperId as string}
            />
          </Grid>

          {/* DG Fields - Visible only when isDG is true */}
          {values.isDG && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 2 }}>
                  Dangerous Goods Information
                </Typography>
              </Grid>

              {/* DG UN ID */}
              <Grid item xs={12} md={6}>
                <SbTextField
                  disabled={!isEditable}
                  required
                  label="DG UN ID"
                  value={values.dgUnId}
                  placeholder="Enter UN ID"
                  onChange={handleChange}
                  name="dgUnId"
                  error={errors.dgUnId as string}
                />
              </Grid>

              {/* DG PI ID */}
              <Grid item xs={12} md={6}>
                <SbTextField
                  disabled={!isEditable}
                  required
                  label="DG PI ID"
                  value={values.dgPiId}
                  placeholder="Enter PI ID"
                  onChange={handleChange}
                  name="dgPiId"
                  error={errors.dgPiId as string}
                />
              </Grid>

              {/* DG Proper Shipping Name */}
              <Grid item xs={12} md={6}>
                <SbTextField
                  disabled={!isEditable}
                  required
                  label="Proper Shipping Name"
                  value={values.dgProperShippingName}
                  placeholder="Enter Proper Shipping Name"
                  onChange={handleChange}
                  name="dgProperShippingName"
                  error={errors.dgProperShippingName as string}
                />
              </Grid>

              {/* DG Class/Division */}
              <Grid item xs={12} md={6}>
                <SbTextField
                  disabled={!isEditable}
                  required
                  label="Class/Division"
                  value={values.dgClassDivision}
                  placeholder="Enter Class/Division"
                  onChange={handleChange}
                  name="dgClassDivision"
                  error={errors.dgClassDivision as string}
                />
              </Grid>

              {/* DG Packing Group */}
              <Grid item xs={12} md={6}>
                <SbTextField
                  disabled={!isEditable}
                  required
                  label="Packing Group"
                  value={values.dgPackingGroup}
                  placeholder="Enter Packing Group"
                  onChange={handleChange}
                  name="dgPackingGroup"
                  error={errors.dgPackingGroup as string}
                />
              </Grid>

              {/* DG Quantity */}
              <Grid item xs={12} md={6}>
                <SbTextField
                  disabled={!isEditable}
                  required
                  label="Quantity"
                  type="string"
                  value={values.dgQuantity}
                  placeholder="Enter Quantity (e.g., 0.5, 1, 2.5)"
                  onChange={e => {
                    // Allow only numbers and decimal point
                    const value = e.target.value;
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      handleChange(e);
                    }
                  }}
                  name="dgQuantity"
                  error={errors.dgQuantity as string}
                  showInputProps={false} // Disable the default number input props
                />
              </Grid>

              {/* DG Quantity Unit */}
              <Grid item xs={12} md={6}>
                <SBDropdown
                  disabled={!isEditable}
                  id="dgQuantityUnit"
                  label="Quantity Unit"
                  required
                  placeholder="Select Unit"
                  name="dgQuantityUnit"
                  value={values.dgQuantityUnit ? [values.dgQuantityUnit] : []}
                  options={dgQuantityUnitOptions}
                  onChange={value => {
                    setFieldValue('dgQuantityUnit', value[0]);
                  }}
                  error={errors.dgQuantityUnit as string}
                />
              </Grid>
            </>
          )}
          {isSaudiArabia && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
                  Saudi Arabia KYC Information
                </Typography>
              </Grid>

              {/* Location Identifier */}
              <Grid item xs={12} md={6}>
                <SbTextField
                  required
                  label="Location Identifier"
                  name="locationIdentifier"
                  value={values.locationIdentifier}
                  onChange={handleChange}
                  error={errors.locationIdentifier as string}
                  disabled={!isEditable}
                />
              </Grid>

              {/* National ID */}
              <Grid item xs={12} md={6}>
                <SbTextField
                  // required
                  label="National ID"
                  name="nationalId"
                  value={values.nationalId}
                  onChange={handleChange}
                  error={errors.nationalId as string}
                  disabled={!isEditable}
                />
              </Grid>

              {/* Date Of Birth */}
              <Grid item xs={12} md={6}>
                <DatePicker
                  // required
                  label="Date of Birth"
                  value={values.dateOfBirth || null}
                  inputFormat={'YYYY-MM-DD'}
                  handleChange={value => {
                    setFieldValue('dateOfBirth', value);
                  }}
                  disabled={!isEditable}
                  maxDate={dayjs()}
                  error={errors.dateOfBirth as string}
                />
              </Grid>

              {/* Nationality */}
              <Grid item xs={12} md={6}>
                <SbTextField
                  // required
                  label="Nationality"
                  name="nationality"
                  value={values.nationality}
                  onChange={handleChange}
                  error={errors.nationality as string}
                  disabled
                />
              </Grid>

              {/* Expiry Date */}
              <Grid item xs={12} md={6}>
                <DatePicker
                  // required
                  label="Expiry Date"
                  inputFormat={'YYYY-MM-DD'}
                  value={values.expiryDate || null}
                  handleChange={value => {
                    setFieldValue('expiryDate', value);
                  }}
                  disabled={!isEditable}
                  error={errors.expiryDate as string}
                />
              </Grid>

              {/* Name As Per ID */}
              <Grid item xs={12} md={6}>
                <SbTextField
                  // required
                  label="Name As Per ID"
                  name="nameAsPerID"
                  value={values.nameAsPerID}
                  onChange={handleChange}
                  error={errors.nameAsPerID as string}
                  disabled={!isEditable}
                />
              </Grid>
            </>
          )}
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <SbButton
              disabled={!isEditable}
              variant="contained"
              sx={{ borderRadius: 1 }}
              fullWidth
              onClick={() => {
                if (emiratesDetails?.trackingNumber) {
                  dispatch(
                    SuccessErrorModalOpen({
                      type: 'success',
                      title: 'Warning',
                      subTitle:
                        'Regenerating the carrier AWB will permanently void the previously generated AWB. Are you sure you want to continue?',
                      closeIcon: true,
                      btnText: 'Proceed',
                      secondaryBtnText: 'Cancel',
                      onConfirm: () => {
                        handleSubmit();
                      },
                    }),
                  );
                } else {
                  handleSubmit();
                }
              }}
            >
              {btnText}
            </SbButton>
          </Grid>

          {onClose && (
            <Grid item xs={12} sm={6} md={3}>
              <SbButton variant="outlined" sx={{ borderRadius: 1 }} fullWidth onClick={onClose}>
                Cancel
              </SbButton>
            </Grid>
          )}
        </Grid>

        {emiratesDetails?.trackingNumber && (
          <Grid container mt={3}>
            <Grid item xs="auto" border="1px solid" className="border-grey font-medium md-text p-2" borderRadius={1}>
              This shipment has been forwarded.
              <Box
                component="span"
                sx={{
                  color: '#485fc7',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  ml: 1,
                }}
                onClick={() => {
                  if (shipmentById?.shipmentNumber) {
                    dispatch(downloadCarrierLabel(shipmentById.shipmentNumber));
                  }
                }}
              >
                Download the carrier AWB
              </Box>
            </Grid>
          </Grid>
        )}
      </Paper>
    </>
  );
};

export default EmiratesInformation;
